/**
 * useEmailAuth.js — Authentification par email (sign-in + sign-up).
 *
 * Couvre les états EMAIL_LOGIN et EMAIL_REGISTER de la machine d'état.
 *
 * Flows gérés :
 *   - signIn  : POST /auth/email-signin { email, password }
 *               → succès : dispatch EMAIL_AUTH_SUCCESS(user)
 *               → erreur : dispatch EMAIL_AUTH_ERROR(err) + erreur inline (pas de navigation)
 *
 *   - signUp  : POST /auth/email-signup { email, password, username }
 *               → succès : dispatch EMAIL_AUTH_SUCCESS(user)
 *               → erreur : dispatch EMAIL_AUTH_ERROR(err) + erreur inline
 *
 * Validation côté client (avant appel API) :
 *   - Email   : format RFC 5321 basique
 *   - Password: min 8 caractères
 *   - Username: /^[a-zA-Z0-9_-]{2,50}$/
 *   - Confirm : correspondance des mots de passe (sign-up uniquement)
 *
 * Les erreurs sont toujours inline (jamais de navigation) — §8.5 SPEC.
 * Aucun import Redux — §8.7.
 */

import { useState, useCallback } from 'react';
import { ACTION_TYPES }          from './useWalletStateMachine';
import { emailSignIn, emailSignUp } from '../services/WalletAuthService';

/* ─── Validation côté client ─────────────────────────────────────────────────── */

const EMAIL_RE   = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const USERNAME_RE = /^[a-zA-Z0-9_-]{2,50}$/;

function validateSignIn({ email, password }) {
  if (!email)               return 'Email address is required.';
  if (!EMAIL_RE.test(email)) return 'Invalid email format.';
  if (!password)            return 'Password is required.';
  return null;
}

function validateSignUp({ username, email, password, confirmPassword }) {
  if (!username)                    return 'Username is required.';
  if (!USERNAME_RE.test(username))  return 'Invalid username (2–50 characters, letters, numbers, _ or -).';
  if (!email)                       return 'Email address is required.';
  if (!EMAIL_RE.test(email))        return 'Invalid email format.';
  if (!password)                    return 'Password is required.';
  if (password.length < 8)          return 'Password must be at least 8 characters.';
  if (password !== confirmPassword) return 'Passwords do not match.';
  return null;
}

/**
 * Calcule la force du mot de passe (0–4).
 * 0 = Weak, 1 = Fair, 2 = Good, 3 = Strong.
 * Critères : longueur, majuscule, chiffre, caractère spécial.
 *
 * @param {string} pwd
 * @returns {0|1|2|3}
 */
export function getPasswordStrength(pwd) {
  if (!pwd || pwd.length < 8) return 0;
  let score = 1; // au moins 8 chars
  if (/[A-Z]/.test(pwd))         score++;
  if (/[0-9]/.test(pwd))         score++;
  if (/[^A-Za-z0-9]/.test(pwd))  score++;
  return Math.min(score, 3);
}

export const PASSWORD_STRENGTH_LABELS = ['Weak', 'Fair', 'Good', 'Strong'];

/**
 * useEmailAuth
 *
 * @param {object}   params
 * @param {function} params.dispatch   - dispatch de useWalletStateMachine
 * @param {string}   params.apiBaseUrl - URL de base de l'API
 *
 * @returns {{
 *   /* sign-in
 *   signInEmail:    string,
 *   setSignInEmail: function,
 *   signInPassword: string,
 *   setSignInPassword: function,
 *   signInLoading:  boolean,
 *   signInError:    string|null,
 *   handleSignIn:   function() => Promise<void>,
 *
 *   /* sign-up
 *   username:           string,
 *   setUsername:        function,
 *   signUpEmail:        string,
 *   setSignUpEmail:     function,
 *   signUpPassword:     string,
 *   setSignUpPassword:  function,
 *   confirmPassword:    string,
 *   setConfirmPassword: function,
 *   signUpLoading:      boolean,
 *   signUpError:        string|null,
 *   passwordStrength:   number,
 *   handleSignUp:       function() => Promise<void>,
 * }}
 */
export function useEmailAuth({ dispatch, apiBaseUrl }) {

  /* ── Champs sign-in ──────────────────────────────────────────────────────── */
  const [signInEmail,    setSignInEmail]    = useState('');
  const [signInPassword, setSignInPassword] = useState('');
  const [signInLoading,  setSignInLoading]  = useState(false);
  const [signInError,    setSignInError]    = useState(null);

  /* ── Champs sign-up ──────────────────────────────────────────────────────── */
  const [username,         setUsername]         = useState('');
  const [signUpEmail,      setSignUpEmail]       = useState('');
  const [signUpPassword,   setSignUpPassword]    = useState('');
  const [confirmPassword,  setConfirmPassword]   = useState('');
  const [signUpLoading,    setSignUpLoading]     = useState(false);
  const [signUpError,      setSignUpError]       = useState(null);

  /* Force du mot de passe (calculée sur signUpPassword) */
  const passwordStrength = getPasswordStrength(signUpPassword);

  /* ── handleSignIn ────────────────────────────────────────────────────────── */
  const handleSignIn = useCallback(async () => {
    setSignInError(null);

    /* Validation côté client */
    const validationError = validateSignIn({ email: signInEmail, password: signInPassword });
    if (validationError) {
      setSignInError(validationError);
      dispatch({ type: ACTION_TYPES.EMAIL_AUTH_ERROR, payload: { message: validationError } });
      return;
    }

    setSignInLoading(true);
    try {
      const user = await emailSignIn(apiBaseUrl, { email: signInEmail, password: signInPassword });
      dispatch({ type: ACTION_TYPES.EMAIL_AUTH_SUCCESS, payload: user });
    } catch (err) {
      const msg = err?.message ?? 'Sign-in failed. Please try again.';
      setSignInError(msg);
      dispatch({ type: ACTION_TYPES.EMAIL_AUTH_ERROR, payload: { message: msg } });
    } finally {
      setSignInLoading(false);
    }
  }, [dispatch, apiBaseUrl, signInEmail, signInPassword]);

  /* ── handleSignUp ────────────────────────────────────────────────────────── */
  const handleSignUp = useCallback(async () => {
    setSignUpError(null);

    /* Validation côté client */
    const validationError = validateSignUp({
      username, email: signUpEmail, password: signUpPassword, confirmPassword,
    });
    if (validationError) {
      setSignUpError(validationError);
      dispatch({ type: ACTION_TYPES.EMAIL_AUTH_ERROR, payload: { message: validationError } });
      return;
    }

    setSignUpLoading(true);
    try {
      // Passer le code de parrainage s'il est présent dans l'URL (?ref=CODE)
      const refCode = new URLSearchParams(window.location.search).get('ref') || undefined;
      const user = await emailSignUp(apiBaseUrl, {
        email: signUpEmail, password: signUpPassword, username,
        ...(refCode ? { ref: refCode } : {}),
      });

      /* Vérification 2FA email requise quand le compte EarniConnect n'est pas encore vérifié.
         La réponse email-signup renvoie { id, email, username, metamask, isVerified, accessToken }.
         Si isVerified === false → on passe par l'étape de saisie du code de vérification.
         Sinon (compte déjà vérifié, cas théorique) → succès direct. */
      if (user && user.isVerified === false) {
        dispatch({ type: ACTION_TYPES.EMAIL_VERIFY_REQUIRED, payload: user });
      } else {
        dispatch({ type: ACTION_TYPES.EMAIL_AUTH_SUCCESS, payload: user });
      }
    } catch (err) {
      const msg = err?.message ?? 'Account creation failed. Please try again.';
      setSignUpError(msg);
      dispatch({ type: ACTION_TYPES.EMAIL_AUTH_ERROR, payload: { message: msg } });
    } finally {
      setSignUpLoading(false);
    }
  }, [dispatch, apiBaseUrl, username, signUpEmail, signUpPassword, confirmPassword]);

  return {
    /* sign-in */
    signInEmail,
    setSignInEmail,
    signInPassword,
    setSignInPassword,
    signInLoading,
    signInError,
    handleSignIn,

    /* sign-up */
    username,
    setUsername,
    signUpEmail,
    setSignUpEmail,
    signUpPassword,
    setSignUpPassword,
    confirmPassword,
    setConfirmPassword,
    signUpLoading,
    signUpError,
    passwordStrength,
    handleSignUp,
  };
}

export default useEmailAuth;
